import { useMemo, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { RecordFactory } from '../../models/RecordFactory'
import { SourceType, capitalizeFirstLetters, determineDefaultSource } from '../../utils'
import { ComponentData } from '../../utils/typing'
import { NavigationBlockerPrompt } from '../NavigationBlockerPrompt'
import { PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import { Checkbox } from '../common/fields/Checkbox'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async () => {
    const sources = await RecordFactory.getInstance().loadSources()

    return { sources }
}

const render = ({
    base,
    data: { sources },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const [user, setUser] = useState(base.me.clone())
    const navigate = usePSNavigate()

    const instantPayoutSource = useMemo(() => {
        const instantSource = sources
            .sort((source) =>
                source.createdDate === undefined ? Infinity : source.createdDate.valueOf()
            )
            .find((source) => source.instant === true)
        if (
            instantSource === undefined ||
            instantSource.cardBrand === undefined ||
            instantSource.cardNumber === undefined
        ) {
            return ''
        }

        return `${capitalizeFirstLetters(instantSource.cardBrand)} - ${instantSource.cardNumber.substring(instantSource.cardNumber.length - 4)}`
    }, [sources])

    const checkingAccountNumber = useMemo(() => {
        const defaultCreditSource = determineDefaultSource(SourceType.Credit, sources)
        if (defaultCreditSource === undefined || defaultCreditSource.accountNumber === undefined) {
            return ''
        }
        return defaultCreditSource.accountNumber.substring(
            defaultCreditSource.accountNumber.length - 4
        )
    }, [sources])

    const hasEdits = user.instantPayout !== base.me.instantPayout

    const handleSave = async () => {
        base.showProgressBar()
        const result = await RecordFactory.getInstance().sendUser(user)
        base.hideProgressBar()

        if (!result.ok) {
            base.logAndDisplayError(result.error)
            return
        }

        base.reload()
        navigate('/settings')
    }

    return (
        <WorkflowPage
            title="Payouts"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" disabled={!hasEdits} onClick={handleSave}>
                        Save
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start">
                <WorkflowPage.Card>
                    <WorkflowPage.IconSection
                        title="Daily instant payouts"
                        icon="dollar.large"
                        hasHoverEffect
                        secondaryView={
                            <Checkbox
                                isChecked={user.instantPayout}
                                onChange={(checked) =>
                                    setUser(user.clone({ instant_payout: checked }))
                                }
                            />
                        }
                    >
                        <div className="space-y-4 text-gray-500">
                            <p>
                                {checkingAccountNumber
                                    ? `Your checking account for payout is Checking - ${checkingAccountNumber}.`
                                    : 'You have not yet setup a checking account for payout.'}{' '}
                                {instantPayoutSource
                                    ? `Your debit card for instant payout is ${instantPayoutSource}`
                                    : 'You have not yet setup a debit card for instant payout.'}
                            </p>

                            {user.instantPayout ? (
                                <p>
                                    Your weekday deposit will be delivered to you within 15 minutes
                                    of being sent for 1% (min $1). You can still cash out
                                    immediately for any eligible payments. If you have an instant
                                    eligible debit card added we will send deposits there. Otherwise
                                    we will send deposits to your bank account.
                                </p>
                            ) : (
                                <>
                                    <p>
                                        Automated regular payouts are run at 12pm Pacific on
                                        weekdays (including holidays), and funds are deposited in
                                        your bank account the next business day (not including
                                        weekends or bank holidays).
                                    </p>
                                    <p>
                                        Want automated payouts to be deposited faster? If you have
                                        an instant transfer eligible debit card, toggle on Daily
                                        instant payouts above. An automated payout will be run
                                        everyday (including weekends) starting at 12pm Pacific as
                                        usual, but the funds will be available in your account
                                        within minutes for an additional 1% (minimum $1 fee).
                                    </p>
                                    <p>
                                        With an eligible debit card, you can also manually initiate
                                        an instant payout at any time using the cash out option in
                                        the Pending payouts dashboard.
                                    </p>
                                </>
                            )}
                        </div>
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <NavigationBlockerPrompt when={hasEdits} />
        </WorkflowPage>
    )
}
export const SetupPayoutsPage = usePSComponent(render, viewWillAppear)
